// faceVoteBuffer.js — multi-frame face embedding accumulator.
//
// ArcFace generates a 512-dim identity vector per frame. A single frame can
// be noisy (slight motion, compression, partial occlusion). By averaging N
// consecutive embeddings of the same face area we get a more stable vector
// that matches the enrolled photo more reliably — especially for faces that
// are slightly turned or partially obscured.
//
// Usage:
//   const buf = createFaceVoteBuffer();
//   const { embedding, frames } = buf.update(face.bbox, face.embedding);
//   // use embedding for matching; `frames` = how many frames contributed

function iou(a, b) {
  // bbox format: [x1, y1, x2, y2] in any consistent coordinate system
  const ix1 = Math.max(a[0], b[0]);
  const iy1 = Math.max(a[1], b[1]);
  const ix2 = Math.min(a[2], b[2]);
  const iy2 = Math.min(a[3], b[3]);
  const iw = Math.max(0, ix2 - ix1);
  const ih = Math.max(0, iy2 - iy1);
  const inter = iw * ih;
  if (!inter) return 0;
  const aA = (a[2] - a[0]) * (a[3] - a[1]);
  const aB = (b[2] - b[0]) * (b[3] - b[1]);
  return inter / (aA + aB - inter + 1e-6);
}

function l2Normalize(arr) {
  let norm = 0;
  for (let i = 0; i < arr.length; i++) norm += arr[i] * arr[i];
  norm = Math.sqrt(norm) || 1;
  return arr.map(v => v / norm);
}

function avgEmbeddings(list) {
  if (!list.length) return null;
  const len = list[0].length;
  const sum = new Array(len).fill(0);
  let used = 0;
  for (const emb of list) {
    // Hanya rata-ratakan vektor yang panjangnya konsisten (cegah korup/beda-dim).
    if (!Array.isArray(emb) || emb.length !== len) continue;
    // PENTING: normalisasi TIAP embedding dulu sebelum dijumlah. Tanpa ini,
    // frame dengan magnitudo lebih besar mendominasi rata-rata (mis. wajah lebih
    // besar/tajam), sehingga fusi jadi bias ke satu frame — kebalikan dari tujuan
    // reduksi noise. Setelah normalisasi, tiap frame berkontribusi setara (centroid
    // arah), lalu hasil penjumlahan dinormalisasi lagi.
    const n = l2Normalize(emb);
    for (let i = 0; i < len; i++) sum[i] += n[i];
    used++;
  }
  if (!used) return null;
  return l2Normalize(sum);
}

export function createFaceVoteBuffer({
  maxFrames = 5,
  iouThreshold = 0.35,
  expireMs = 6000,
} = {}) {
  let tracks = [];

  return {
    update(bbox, embedding, nowMs = Date.now()) {
      if (!Array.isArray(bbox) || !Array.isArray(embedding) || !embedding.length) {
        return { embedding, frames: 1 };
      }

      tracks = tracks.filter(t => nowMs - t.lastSeen < expireMs);

      let best = null;
      let bestScore = 0;
      for (const t of tracks) {
        const score = iou(bbox, t.bbox);
        if (score > bestScore && score >= iouThreshold) {
          bestScore = score;
          best = t;
        }
      }

      if (best) {
        best.bbox = bbox;
        best.lastSeen = nowMs;
        best.embeddings.push(embedding);
        if (best.embeddings.length > maxFrames) best.embeddings.shift();
      } else {
        best = { bbox, embeddings: [embedding], lastSeen: nowMs };
        tracks.push(best);
      }

      return {
        embedding: avgEmbeddings(best.embeddings) || embedding,
        frames: best.embeddings.length,
      };
    },

    clear() { tracks = []; },
  };
}
